/**
 * Scheduled / due date handling for procedures.
 *
 * **Mirrors `cgt-backend/src/procedures/planning-dates.ts` and must stay in step
 * with it.** The server is the authority — it derives and it validates — but the
 * pickers here have to constrain and display against the same rules, or the
 * screen offers times the save then rejects.
 *
 * Pure and dependency-free so it is unit testable: vitest here is node-only and
 * matches `src/**\/*.test.ts`.
 */

const MS_PER_DAY = 86_400_000

/**
 * Does this instant carry a real time of day?
 *
 * `scheduledDate` / `dueDate` were date-only columns until z8nrz7cthg, so every
 * row predating the change reads back as exactly midnight UTC. That is the only
 * available signal for "nobody chose a time", short of a precision column.
 *
 * **A heuristic with one false negative**, same as the server's: a procedure
 * genuinely scheduled for exactly 00:00 UTC renders as date-only. Cosmetic —
 * the stored instant is correct and nothing computes from this.
 */
export function hasTimeOfDay(iso: string | null | undefined): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return false
  return t % MS_PER_DAY !== 0
}

/**
 * An instant as a `datetime-local` input value (`YYYY-MM-DDTHH:mm`), in LOCAL
 * time — which is what that input expects and what the operator reads.
 *
 * Empty string for absent or unparseable, because that is what clears the
 * control; returning null would set it to the string "null".
 */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => `${n}`.padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** A `datetime-local` value back to an ISO instant. Null for empty/invalid. */
export function fromLocalInput(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/**
 * A `datetime-local` value split into the two controls that now edit it.
 *
 * The picker is a calendar plus a `time` field, but the draft it edits stays one
 * `datetime-local` string so every helper here — and the commit path — keeps
 * working on the shape it already understood. Splitting on the literal `T`
 * rather than going through `Date` keeps a half-finished draft intact: a day
 * chosen before a time is `2027-01-01T`, which `new Date` cannot represent and
 * would round-trip to empty, throwing away the day just picked.
 */
export function splitLocalInput(local: string): { date: string; time: string } {
  const at = local.indexOf('T')
  if (at === -1) return { date: local, time: '' }
  return { date: local.slice(0, at), time: local.slice(at + 1) }
}

/**
 * The two controls back into one draft.
 *
 * Empty whenever the DATE is missing: a time with no day is not a partial
 * instant, it is nothing, and `''` is what the rest of this module reads as
 * "unset". A date with no time is kept as a partial draft on purpose — it is a
 * real intermediate state of the picker, and `isCompleteLocalInput` is what
 * stops it being committed.
 */
export function joinLocalInput(date: string, time: string): string {
  if (!date) return ''
  return `${date}T${time}`
}

/** Both halves present — what `datetime-local` used to enforce on its own. */
export function isCompleteLocalInput(local: string): boolean {
  const { date, time } = splitLocalInput(local)
  return Boolean(date) && /^\d{2}:\d{2}/.test(time)
}

/**
 * Would this draft land before the earliest selectable instant?
 *
 * The native input enforced `min` for us across BOTH halves at once; two
 * separate controls do not, and the gap is not cosmetic. Picking today when the
 * floor is "now" leaves every earlier hour selectable in the time field, so a
 * run could be planned into the past and the save would bounce off the server's
 * own check. Compared as instants, not strings, so it holds across the
 * date/time boundary.
 *
 * False for an incomplete or unparseable draft — there is no instant to judge
 * yet, and `isCompleteLocalInput` is what gates those.
 */
export function isBeforeMin(local: string, minIso: string | null | undefined): boolean {
  if (!minIso || !isCompleteLocalInput(local)) return false
  const at = new Date(local).getTime()
  const floor = new Date(minIso).getTime()
  if (Number.isNaN(at) || Number.isNaN(floor)) return false
  return at < floor
}

/**
 * How a planning date reads on screen.
 *
 * Date-only for values that carry no time, so a legacy row does not claim
 * someone scheduled it for midnight. Returns an em dash for absent, so callers
 * do not each invent their own placeholder.
 */
export function formatPlanned(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const date = d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
  if (!hasTimeOfDay(iso)) return date
  return `${date}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

/**
 * The due date a schedule implies — the client-side twin of the server's
 * `deriveDueDate`, for previewing a value before it round-trips.
 *
 * Adds calendar days while preserving the time of day, so an offset spanning a
 * daylight-saving boundary still lands at the same wall-clock time. Null when
 * there is nothing to derive from; a zero offset is meaningful (due same day)
 * and is not treated as absent.
 */
export function deriveDue(
  scheduledIso: string | null | undefined,
  offsetDays: number | null | undefined,
): string | null {
  if (!scheduledIso || offsetDays == null) return null
  const d = new Date(scheduledIso)
  if (Number.isNaN(d.getTime())) return null
  d.setDate(d.getDate() + offsetDays)
  return d.toISOString()
}
